import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'

import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import {
	appendCurrentUserNotifications,
	countUnreadNotifications,
	getNotifications,
	markAllNotificationsRead,
	readNotification,
	updateCurrentUserNotifications,
} from '../../features/notificationSlice'
import styles from './afterLoginNavbar.module.scss'
import BreadcrumbCustom from '../atoms/breadcrumb/BreadcrumbCustom'
import { Button, ListItemIcon, Menu, MenuItem } from '@mui/material'
import { toggleSidebar } from '../../features/sidebarSlice'
import { getTimePassed } from '../../util/getTime'
import { setSettingsTab } from '../../features/authSlice'
import { toastHelper } from '../../util/toastHelper'
import { getNotificationIcon, isRiskAlertNotification } from '../../util/notificationIconHelper'

const NOTIFICATION_MENU_STYLE = {
	marginTop: '18px',
	maxWidth: '340px',
	'& .MuiPaper-root': {
		boxShadow: '0px 0px 4px 0px #00000040',
		border: '1px solid #DFE4EB',
		borderRadius: '12px',
		minWidth: '100px',
		width: 'max-content !important',
		minWidth: '340px'
	},
	'& .MuiList-root': { padding: '16px 12px !important' },
	'& .MuiButtonBase-root': {
		padding: '0 !important',
		marginBottom: '12px !important',
		backgroundColor: '#fff !important',
	},
	'& .MuiDivider-root': { margin: '0 !important' },
}

export const UserNavbar = () => {
	const [anchorEl, setAnchorEl] = useState(null)
	const [markingAll, setMarkingAll] = useState(false)
	const open = Boolean(anchorEl)

	const dispatch = useDispatch()
	const navigate = useNavigate()

	const user = useSelector((state) => state.auth.user)
	const numUnreadNotifications = useSelector(
		(state) => state.notification.numUnreadNotifications
	)
	const notifications = useSelector(
		(state) => state.notification.notifications || []
	)
	const socket = useSelector((state) => state.auth.socket)

	useEffect(() => {
		dispatch(getNotifications())
		dispatch(countUnreadNotifications())

		const handlerSingle = (notification) => {
			const receiverId =
				notification?.receiverId?.toString?.() ?? notification?.receiverId
			if (receiverId !== user?._id?.toString?.()) return
			dispatch(updateCurrentUserNotifications(notification))
			if (!isRiskAlertNotification(notification)) {
				toastHelper(notification, 'notification')
			}
		}

		const handlerBatch = (payload) => {
			const list = payload?.notifications
			if (!Array.isArray(list) || list.length === 0) return
			const currentUserId = user?._id?.toString?.()
			const forCurrentUser = list.filter(
				(n) =>
					(n?.receiverId?.toString?.() ?? n?.receiverId) === currentUserId
			)
			if (forCurrentUser.length === 0) return
			dispatch(appendCurrentUserNotifications({ notifications: forCurrentUser }))
			if (forCurrentUser.some(isRiskAlertNotification)) {
				toastHelper(
					{
						message: `You have ${forCurrentUser.length} new risk alert${forCurrentUser.length > 1 ? 's' : ''}`,
						type: 'riskAlertsSummary',
						link: '/notifications',
					},
					'notification'
				)
			} else if (forCurrentUser.length > 0) {
				toastHelper(forCurrentUser[0], 'notification')
			}
		}

		socket?.on?.('newUserNotification', handlerSingle)
		socket?.on?.('newUserNotifications', handlerBatch)
		return () => {
			socket?.off?.('newUserNotification', handlerSingle)
			socket?.off?.('newUserNotifications', handlerBatch)
		}
	}, [dispatch, socket, user?._id])

	const crumbs = useBreadcrumbs()
	const breadcrumbs = crumbs.map((crumb, i) => {
		const isLast = i === crumbs.length - 1
		if (crumb.to && !isLast) {
			return (
				<Link key={i} to={crumb.to} style={{ color: '#7ca6bd', textDecoration: 'none' }}>
					{crumb.label}
				</Link>
			)
		}
		return (
			<Typography key={i} sx={{ color: '#7ca6bd' }}>{crumb.label}</Typography>
		)
	})

	const handleSidebarExpand = () => {
		dispatch(toggleSidebar())
	}

	const toMenuItem = (n) => {
		const heading = isRiskAlertNotification(n) ? 'Risk Alert' : (n?.type || 'Notification')
		return {
			id: n._id,
			icon: getNotificationIcon(n),
			heading,
			subheading: n?.message || '',
			time: getTimePassed(n?.createdAt),
			link: n?.link,
			status: n?.status,
			senderId: n?.senderId,
			type: n?.type,
		}
	}

	const subscriptionTypes = [
		'Business Basic Subscription',
		'Business Pro Subscription',
		'Top Vendor Badge',
	]

	const topItems = useMemo(
		() => notifications.filter(n => n.status === 'unread').slice(0, 4).map(toMenuItem),
		[notifications]
	)

	const handleNotificationClick = async (event) => {
		setAnchorEl(event.currentTarget)
	}

	const handleNotificationClose = () => setAnchorEl(null)

	const handleNotificationSelect = async (item) => {
		if (item?.status === 'unread') {
			dispatch(readNotification(item.id))
		}
		handleNotificationClose()
		if (item?.link) {
			if (subscriptionTypes.includes(item?.heading)) {
				dispatch(setSettingsTab(4))
			}

			if (item?.type === 'Chat' || item?.heading === 'Chat') {
				try {
					const url = new URL(item.link, window.location.origin)
					const groupId = url.searchParams.get('groupId')

					if (groupId) {
						navigate(url.pathname, { state: { selectedGroupId: groupId } })
					} else {
						navigate(item.link, { state: { selectedUserId: item.senderId } })
					}
				} catch (e) {
					navigate(item.link, { state: { selectedUserId: item.senderId } })
				}
			} else {
				navigate(item.link)
			}
		}
	}

	const handleMarkAllRead = async () => {
		if (markingAll || !notifications.length) return
		setMarkingAll(true)
		try {
			const unread = notifications.filter((n) => n.status === 'unread')
			if (!unread.length) return
			const unreadIds = unread.map((n) => n._id)
			await dispatch(markAllNotificationsRead(unreadIds))
		} finally {
			setMarkingAll(false)
		}
	}

	return (
		<Box className={`${styles.afterLoginNavbar} afterLoginNavbar `}>
			<header className={styles.navbarWrp}>
				<Toolbar disableGutters>
					<Box className={styles.navLeft}>
						<Button
							className={styles.panelIcon}
							data-cy="toggle-sidebar"
							onClick={handleSidebarExpand}
						>
							<img
								src="/images/PanelLeft.svg"
								alt="PanelLeft.svg"
							/>
						</Button>
						<BreadcrumbCustom className={styles.breadcrumbs}>
							{breadcrumbs}
						</BreadcrumbCustom>
					</Box>

					<Box className={styles.navRight}>
						<ul className={styles.rightNavButtons}>
							<li className={styles.notificationDropdown}>
								<IconButton
									data-cy="notification-button"
									aria-label="menu"
									aria-controls={
										open ? 'dropdown-menu' : undefined
									}
									aria-haspopup="true"
									onClick={handleNotificationClick}
									disableRipple
									className={styles.notificationIcon}
								>
									<Badge
										badgeContent={numUnreadNotifications}
										color="error"
										sx={{
											'&.MuiBadge-root': {
												width: '20px',
												height: '20px',
											},
											'& .MuiBadge-badge': {
												minWidth: '16px',
												height: '16px',
												padding: '0 4px',
												fontSize: '10px',
												fontWeight: '600',
												fontFamily: 'Inter',
												backgroundColor: '#F44336',
												color: '#fff',
												top: '2px',
												right: '2px',
											},
										}}
									>
										<img
											src="/images/notification.svg"
											alt="notification"
											className={styles.notificationImg}
										/>
									</Badge>
								</IconButton>

								<Menu
									id="dropdown-menu"
									anchorEl={anchorEl}
									open={open}
									onClose={handleNotificationClose}
									slotProps={{ paper: { style: {} } }}
									sx={NOTIFICATION_MENU_STYLE}
									className={styles.menuContainer}
								>
									<div className={styles.menuHeader}>
										<Typography variant="h5">
											Notifications
										</Typography>
										<Typography
											data-cy="mark-as-read"
											variant="h6"
											sx={{
												cursor: markingAll
													? 'not-allowed'
													: 'pointer',
												opacity: markingAll ? 0.6 : 1,
											}}
											onClick={handleMarkAllRead}
										>
											{markingAll
												? 'Marking…'
												: 'Mark all as read'}
										</Typography>
									</div>

									{topItems.map((option) => (
										<MenuItem
											data-cy="notification-tab"
											key={option.id}
											onClick={() =>
												handleNotificationSelect(option)
											}
											className={styles.menuBody}
										>
											<div
												className={`${styles.menuItemCard} ${option.status === 'unread' ? styles.unread : ''}`}
											>
												<ListItemIcon
													className={styles.menuIcon}
												>
													{option.icon}
												</ListItemIcon>
												<div
													className={styles.menuText}
												>
													<div className={styles.row}>
														<Typography variant="h5">
															{option.heading}
														</Typography>
														<Typography
															variant="h6"
															className={
																styles.time
															}
														>
															{option.time}
														</Typography>
													</div>
													<p
														className={
															styles.subheading
														}
													>
														{option.subheading}
													</p>
												</div>
											</div>
										</MenuItem>
									))}

									<div className={styles.menuFooter}>
										<Link
											to="/notifications"
											className={styles.viewAll}
											data-cy="showAllNotifications"
										>
											View all Notifications
										</Link>
									</div>
								</Menu>
							</li>

							<li>
								<IconButton
									size="small"
									edge="end"
									onClick={() => navigate('/settings')}
									color="inherit"
								>
									<Avatar
										data-cy="user-profile"
										src={user.profilePic}
										sx={{ width: 32, height: 32 }}
									/>
								</IconButton>
							</li>
						</ul>
					</Box>
				</Toolbar>
			</header>
		</Box>
	)
}
